import clsx from "clsx";
import L from "leaflet";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import ContainerDimensions from "react-container-dimensions";
import { useTranslation } from "react-i18next";
import { Map, Marker, Polygon, Popup, TileLayer, Tooltip } from "react-leaflet";
import { Link } from "react-router-dom";

import { Button, Grid, Typography, makeStyles, useTheme } from "@material-ui/core";

import { getBoundsFromLatLng, leafletURL } from "../../services/map.service";
import { StringUtils } from "../../util";

const ANIMATE_MAP_ZOOMING = false;

const useStyles = makeStyles((theme) => ({
  markerIcon: {
    width: "5px",
    height: "5px",
    borderRadius: "50%",
    border: "1px solid white",
    opacity: "0.7",
  },
  offline: { backgroundColor: theme.palette.statusColors.offline },
  available: { backgroundColor: theme.palette.statusColors.available },
  reserved: { backgroundColor: theme.palette.statusColors.reserved },
  fault: { backgroundColor: theme.palette.statusColors.fault },
  unavailable: { backgroundColor: theme.palette.statusColors.unavailable },
  operator: { backgroundColor: theme.palette.statusColors.operator },
  self_check: { backgroundColor: theme.palette.statusColors.self_check },
  firmware_update: { backgroundColor: theme.palette.statusColors.firmware_update },
  out_of_service: { backgroundColor: theme.palette.statusColors.out_of_service },
  charging: { backgroundColor: theme.palette.statusColors.charging },
  mismatched: { backgroundColor: theme.palette.statusColors.mismatched },
}));

const FleetMap = ({ vehiclesList, updateOnChangeTo, fleetId, defaultBounds, geofences }) => {
  const [mapCenter, setMapCenter] = useState(null);
  const theme = useTheme();
  const classes = useStyles(theme);
  const { t } = useTranslation();

  const getToolTip = (name, properties) => {
    const { label, incentive } = properties;
    const tip = (
      <span>
        {name && (
          <span>
            {name}
            <br />
          </span>
        )}
        {label}
        {incentive?.amount && (
          <span>
            <br />
            {StringUtils.formatNumber(incentive.amount, incentive?.currency)}
          </span>
        )}
      </span>
    );
    return <Tooltip opacity={0.75}>{tip}</Tooltip>;
  };

  useEffect(() => {
    setMapCenter(null);
  }, [updateOnChangeTo]);

  const getBounds = () => {
    if (mapCenter) return getBoundsFromLatLng(mapCenter);
    let bounds;
    vehiclesList.forEach((v) => {
      const lastPos = getLastVehiclePosition(v);
      if (lastPos) {
        if (!bounds)
          bounds = L.latLngBounds([
            [lastPos.coordinates[1], lastPos.coordinates[0]],
            [lastPos.coordinates[1], lastPos.coordinates[0]],
          ]);
        bounds.extend([lastPos.coordinates[1], lastPos.coordinates[0]]);
      }
    });
    if (!bounds) return defaultBounds;
    if (bounds.getSouthWest().equals(bounds.getNorthEast()))
      return getBoundsFromLatLng(bounds.getSouthWest());
    return bounds;
  };

  const getLastVehiclePosition = (v) => {
    if (v.last_position && v.last_position.coordinates && v.last_position.coordinates.length > 1) {
      if (
        v.last_scan_location &&
        v.last_scan_location.coordinates &&
        v.last_scan_location.coordinates.length > 1 &&
        v.last_scan_time > v.last_ingest
      ) {
        return v.last_scan_location;
      }
      return v.last_position;
    }
    return null;
  };

  return (
    <ContainerDimensions>
      {({ width, height }) => (
        <Map
          bounds={getBounds()}
          boundsOptions={{ padding: [10, 10] }}
          doubleClickZoom
          scrollWheelZoom
          dragging
          keyboard
          animate
          touchZoom
          useFlyTo={ANIMATE_MAP_ZOOMING}
          style={{ height: width > height ? width / 2 : width }}
        >
          <TileLayer
            url={leafletURL}
            attribution={`&copy; Superpedestrian ${new Date().getFullYear()}`}
          />
          {geofences &&
            geofences.map((g) => {
              let fenceProperties = null;
              if (g.is_operational_zone) {
                fenceProperties = {
                  color: theme.palette.geofencesTypeColors.is_operational_zone,
                  label: t("Fleet.Map.Geofences.is_operational_zone"),
                };
              } else if (g.rules.length) {
                // rules array has been filtered for active rules, and only one rule should be active at a time
                fenceProperties = {
                  color: theme.palette.geofencesTypeColors[g.rules[0].label],
                  label: t(`Fleet.Map.Geofences.${g.rules[0].label}`),
                  incentive: g.rules[0]?.parking_incentive,
                };
              }
              if (fenceProperties && g.coordinates) {
                const isPointPolygon = g.polygon?.geometry?.type === "Point";
                const position = isPointPolygon ? [g.coordinates] : g.coordinates;
                const weight = isPointPolygon ? 8 : 2;
                return (
                  <Polygon
                    key={g.id}
                    interactive
                    positions={position}
                    color={fenceProperties.color}
                    weight={weight}
                    fill={!g.is_operational_zone}
                  >
                    {getToolTip(g.name, fenceProperties)}
                  </Polygon>
                );
              }
              return null;
            })}
          {vehiclesList.filter(getLastVehiclePosition).map((v) => (
            <Marker
              position={[
                getLastVehiclePosition(v).coordinates[1],
                getLastVehiclePosition(v).coordinates[0],
              ]}
              icon={L.divIcon({
                className: clsx(
                  classes.markerIcon,
                  classes[v.has_state_mismatch ? "mismatched" : v.target_state],
                ),
              })}
              key={v.id}
            >
              <Popup
                onOpen={() => {
                  setMapCenter(
                    L.latLng(
                      getLastVehiclePosition(v).coordinates[1],
                      getLastVehiclePosition(v).coordinates[0],
                    ),
                  );
                  getBounds();
                }}
              >
                <Grid container justify="space-between">
                  <Grid item>
                    <Typography variant="inherit">
                      {t("Fleet.Map.Vehicle")}:{" "}
                      <strong>
                        {v.shortcodes && v.shortcodes[0] ? v.shortcodes[0].shortcode : null}
                      </strong>
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="inherit">
                      {t("Fleet.Map.Status")}:{" "}
                      <strong>
                        {t(
                          `Vehicles.Detail.Statuses.${
                            v.has_state_mismatch ? "mismatched" : v.target_state
                          }`,
                        )}
                      </strong>
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="inherit">
                      {t("Fleet.Map.Battery")}: <strong>{v.battery_charge}%</strong>
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="inherit">
                      {t("Fleet.Map.Cellular")}: <strong>{v.cell_bars}/4</strong>
                    </Typography>
                  </Grid>
                </Grid>
                <Link to={`/fleets/${fleetId}/vehicles/${v.id}`}>
                  <Button size="small" color="primary" fullWidth>
                    {t("Fleet.Map.View")}
                  </Button>
                </Link>
              </Popup>
            </Marker>
          ))}
        </Map>
      )}
    </ContainerDimensions>
  );
};

FleetMap.propTypes = {
  vehiclesList: PropTypes.array.isRequired,
  updateOnChangeTo: PropTypes.any.isRequired,
  fleetId: PropTypes.string.isRequired,
  defaultBounds: L.LatLngBounds,
  geofences: PropTypes.array,
};

FleetMap.defaultProps = {
  defaultBounds: L.latLngBounds([
    [42.35, -71.11],
    [42.37, -71.06],
  ]),
  geofences: [],
};

export default FleetMap;
